/**
 * Decides whether an error is worth spending one of our Session Replays on.
 *
 * A buffered ("on error") replay is flushed by the SDK's `beforeSendEvent`
 * hook, which runs *after* `ignoreErrors` (inbound filters) but *before* the
 * `beforeSend` callback in `Sentry.init`. So any error we drop as an issue in
 * `beforeSend` has already spent one of our replays by the time it's dropped —
 * producing replays with zero linked errors that quietly exhaust the free-tier
 * budget (50/mo). `beforeErrorSampling` is the only veto that runs at flush
 * time, so it must mirror the `beforeSend` drops: a replay is worth a slot only
 * when the triggering error is one we actually keep as an issue.
 *
 * Pure function — unit-tested in `__tests__/sentry-replay-filter.test.ts`.
 *
 * @alias replay sampling, beforeErrorSampling, replay budget, replay noise
 */

/** Minimal shape of the Sentry error event fields we inspect. */
type ReplayErrorEvent = {
  message?: string;
  exception?: { values?: Array<{ value?: string }> };
};

// Expired / missing auth session (401/403). The router already refreshes the
// token and redirects to /login, so these are pure noise — the same condition
// `isAuthError` drops in `beforeSend`. Digit boundaries avoid matching an
// unrelated number that merely contains 401/403 (e.g. a "1403ms" duration).
const AUTH_NOISE =
  /(?:^|\D)(?:401|403)(?:\D|$)|authentication required|unauthorized/i;

// Blocked third-party analytics whose fetch failures we also drop in `beforeSend`.
const BLOCKED_ANALYTICS = /sitebehaviour\.com/i;

/**
 * Returns `false` for known-noise errors (auth-session expiry, blocked
 * analytics) so the SDK skips capturing a replay for them; `true` otherwise.
 */
export function isReplayWorthyError(event: ReplayErrorEvent): boolean {
  const messages = [
    event.message ?? "",
    ...(event.exception?.values?.map((v) => v.value ?? "") ?? []),
  ];
  return !messages.some(
    (message) => AUTH_NOISE.test(message) || BLOCKED_ANALYTICS.test(message),
  );
}
